import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  LogOut,
  Plus,
  Menu,
  X,
  FileEdit,
  Package,
  FolderKanban,
  Shield,
  AlertTriangle,
  HelpCircle,
  Receipt,
  Calculator,
  Bell,
  Repeat,
  ShoppingBag,
  UsersRound,
  UserCog,
  Wallet,
  CalendarDays,
  BookOpen,
  BarChart3,
  Rocket,
  Lock,
  MessageCircle,
  Ticket,
  ChevronDown,
  ClipboardList,
  Coins,
  TrendingUp
} from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect, useCallback } from 'react';
import GlobalSearch from './GlobalSearch';
import LanguageSelector from './LanguageSelector';
import HelpChatbot from './HelpChatbot';
import OnboardingTour, { startOnboarding } from './OnboardingTour';
import { usePlanAccess } from '../hooks/usePlanAccess';
import { getUnreadTicketCount, adminGetUnreadCount } from '../lib/api';

const Layout = () => {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { plan, trial, hasAccess } = usePlanAccess();
  const [unreadTickets, setUnreadTickets] = useState(0);

  const isAdmin = user?.role === 'admin';
  const isTrial = user?.status === 'trial';
  const isExpired = user?.status === 'expired' || user?.status === 'suspended';

  const fetchUnread = useCallback(async () => {
    try {
      if (isAdmin) {
        const res = await adminGetUnreadCount();
        setUnreadTickets(res.data.unread_count || 0);
      } else {
        const res = await getUnreadTicketCount();
        setUnreadTickets(res.data.unread_count || 0);
      }
    } catch { /* empty */ }
  }, [isAdmin]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const planLabel = { starter: 'Starter', pro: 'Pro', enterprise: 'Tout-en-un' };
  const planColor = { starter: 'bg-slate-600', pro: 'bg-blue-600', enterprise: 'bg-amber-600' };

  const [openSections, setOpenSections] = useState({ finance: false, hr: false, integrations: false });

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Structured nav sections
  const mainNav = [
    { to: '/app/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard', module: 'dashboard' },
    { to: '/app/invoices', icon: FileText, labelKey: 'nav.invoices', module: 'invoices' },
    { to: '/app/recurring', icon: Repeat, labelKey: 'nav.recurring', module: 'recurring', sub: true },
    { to: '/app/quotes', icon: FileEdit, labelKey: 'nav.quotes', module: 'quotes' },
    { to: '/app/clients', icon: Users, labelKey: 'nav.clients', module: 'clients' },
  ];

  const financeNav = [
    { to: '/app/products', icon: Package, labelKey: 'nav.products', module: 'products' },
    { to: '/app/projects', icon: FolderKanban, labelKey: 'nav.projects', module: 'projects' },
    { to: '/app/expenses', icon: Receipt, labelKey: 'nav.expenses', module: 'expenses' },
    { to: '/app/revenues', icon: Coins, labelKey: 'nav.revenues', module: 'revenues' },
    { to: '/app/projections', icon: TrendingUp, labelKey: 'nav.projections', module: 'projections' },
    { to: '/app/fiscal', icon: Calculator, labelKey: 'nav.fiscal', module: 'fiscal' },
    { to: '/app/accounting', icon: BookOpen, labelKey: 'nav.accounting', module: 'accounting' },
  ];

  const hrNav = [
    { to: '/app/hr-dashboard', icon: BarChart3, labelKey: 'nav.hrDashboard', module: 'hr-dashboard' },
    { to: '/app/employees', icon: UserCog, labelKey: 'nav.employees', module: 'employees' },
    { to: '/app/payslips', icon: Wallet, labelKey: 'nav.payslips', module: 'payslips' },
    { to: '/app/absences', icon: CalendarDays, labelKey: 'nav.absences', module: 'absences' },
  ];

  const integrationNav = [
    { to: '/app/shopify', icon: ShoppingBag, labelKey: 'nav.shopify', module: 'shopify' },
    { to: '/app/team', icon: UsersRound, labelKey: 'nav.team', module: 'team' },
    { to: '/app/reminders', icon: Bell, labelKey: 'nav.reminders', module: 'reminders' },
    { to: '/app/audit-log', icon: ClipboardList, label: 'Journal', module: 'dashboard' },
  ];

  const bottomNav = [
    { to: '/app/support', icon: MessageCircle, labelKey: 'nav.support', module: 'support', badge: !isAdmin ? unreadTickets : 0 },
    ...(isAdmin ? [{ to: '/app/admin/tickets', icon: Ticket, labelKey: 'nav.adminTickets', module: 'support', badge: unreadTickets }] : []),
    { to: '/app/settings', icon: Settings, labelKey: 'nav.settings', module: 'settings' },
    { to: '/app/faq', icon: HelpCircle, labelKey: 'nav.help', module: 'faq' },
    ...(isAdmin ? [{ to: '/app/admin', icon: Shield, labelKey: 'nav.admin', module: 'dashboard' }] : []),
  ];

  const sectionLabels = {
    finance: { fr: 'Finance', de: 'Finanzen', en: 'Finance' },
    hr: { fr: 'RH & Paie', de: 'HR & Lohn', en: 'HR & Payroll' },
    integrations: { fr: 'Outils', de: 'Tools', en: 'Tools' },
  };

  const NavItem = ({ item }) => {
    const locked = !hasAccess(item.module);
    if (locked) {
      return (
        <button
          key={item.to}
          onClick={() => { navigate(item.to); setSidebarOpen(false); }}
          className="sidebar-link w-full text-left opacity-40 cursor-pointer"
          data-testid={`nav-${item.to.slice(1)}`}
        >
          <item.icon className="w-4 h-4" />
          <span className="flex-1 text-[13px]">{item.labelKey ? t(item.labelKey) : item.label}</span>
          <Lock className="w-3 h-3 text-zinc-600" />
        </button>
      );
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
        data-testid={`nav-${item.to.slice(1)}`}
      >
        <item.icon className="w-4 h-4" />
        <span className="flex-1 text-[13px]">{item.labelKey ? t(item.labelKey) : item.label}</span>
        {item.badge > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
            {item.badge}
          </span>
        )}
      </NavLink>
    );
  };

  const SectionHeader = ({ sectionKey, icon: Icon }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      className="w-full flex items-center justify-between px-3 py-1.5 mt-2 mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-400 transition-colors"
      data-testid={`section-${sectionKey}`}
    >
      <span>{sectionLabels[sectionKey][language] || sectionLabels[sectionKey].fr}</span>
      <ChevronDown className={`w-3 h-3 transition-transform ${openSections[sectionKey] ? 'rotate-180' : ''}`} />
    </button>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Always dark */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 border-r bg-[#111113] border-white/10
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/xyron-logo.png" alt="Xyron Core ERP" className="h-8 w-8 rounded-lg object-contain" />
                <h1 className="font-heading text-xl font-bold text-white">
                  Xyron<span className="text-[#00F0FF]">Core ERP</span>
                </h1>
              </div>
              <button 
                className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm mt-1 text-zinc-500">{t('sidebar.invoiceManagement')}</p>
          </div>

          {/* Plan Badge & Trial/Subscription Status */}
          {!isAdmin && (
            <div className="px-4 pt-4">
              {/* Plan badge */}
              <div className={`p-3 rounded-lg mb-2 ${
                isTrial ? 'bg-blue-900/30 border border-blue-500/30' :
                user?.status === 'active' ? 'bg-emerald-900/30 border border-emerald-500/30' :
                'bg-amber-900/30 border border-amber-500/30'
              }`} data-testid="plan-badge">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${planColor[plan] || 'bg-slate-600'} text-white`}>
                    {planLabel[plan] || plan}
                  </span>
                  {isTrial && trial?.days_left !== undefined && (
                    <span className="text-xs text-blue-400">{trial.days_left}j restants</span>
                  )}
                </div>
                {isTrial && (
                  <p className="text-xs text-zinc-400 mt-1">
                    {trial?.days_left !== undefined ? `${trial.days_left}j restants` : 'Essai gratuit'}
                  </p>
                )}
                {(isTrial || isExpired) && (
                  <Button 
                    variant="link" 
                    className="text-xs text-blue-400 p-0 h-auto mt-1 hover:text-blue-300"
                    onClick={() => navigate('/pricing')}
                  >
                    {isTrial ? t('sidebar.viewPlans') : t('sidebar.subscribeNow')} →
                  </Button>
                )}
                {user?.status === 'active' && user?.subscription_expires && (
                  <p className="text-xs text-emerald-400 mt-1">
                    {t('sidebar.expiresOn')} {user.subscription_expires.slice(0, 10)}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="p-4 space-y-2">
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                navigate('/app/invoices/new');
                setSidebarOpen(false);
              }}
              disabled={isExpired}
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sidebar.newInvoice')}
            </Button>
            <Button 
              variant="outline"
              className="w-full border-zinc-600 text-white hover:bg-zinc-700 hover:text-white"
              onClick={() => {
                navigate('/app/quotes/new');
                setSidebarOpen(false);
              }}
              disabled={isExpired}
              data-testid="create-quote-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sidebar.newQuote')}
            </Button>
          </div>

          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
            {/* Main */}
            {mainNav.map(item => <NavItem key={item.to} item={item} />)}

            {/* Finance */}
            <SectionHeader sectionKey="finance" />
            {openSections.finance && financeNav.map(item => <NavItem key={item.to} item={item} />)}

            {/* RH & Paie */}
            <SectionHeader sectionKey="hr" />
            {openSections.hr && hrNav.map(item => <NavItem key={item.to} item={item} />)}

            {/* Outils / Intégrations */}
            <SectionHeader sectionKey="integrations" />
            {openSections.integrations && integrationNav.map(item => <NavItem key={item.to} item={item} />)}

            {/* Separator */}
            <div className="border-t border-white/5 my-2" />

            {/* Bottom items */}
            {bottomNav.map(item => <NavItem key={item.to} item={item} />)}

            <button
              onClick={() => { startOnboarding(); setSidebarOpen(false); }}
              className="sidebar-link w-full text-left"
              data-testid="start-onboarding-btn"
            >
              <Rocket className="w-4 h-4" />
              <span className="text-[13px]">{language === 'de' ? 'Anleitung' : language === 'en' ? 'Quick guide' : 'Guide rapide'}</span>
            </button>
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAdmin ? 'bg-purple-900/30' : 'bg-blue-900/30'}`}>
                {isAdmin ? (
                  <Shield className="w-5 h-5 text-purple-400" />
                ) : (
                  <span className="font-semibold text-sm text-blue-400">
                    {user?.company_name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-white">
                  {user?.company_name}
                </p>
                <p className="text-xs truncate text-zinc-500">
                  {isAdmin ? t('sidebar.administrator') : user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm transition-colors text-zinc-400 hover:text-red-400"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-h-screen bg-slate-50">
        {/* Desktop header with search */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 bg-white border-b border-slate-200">
          <GlobalSearch />
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <LanguageSelector variant="minimal" />
            {user?.company_name}
          </div>
        </header>
        
        {/* Mobile header */}
        <header className="lg:hidden border-b p-4 flex items-center gap-4 bg-white border-slate-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <img src="/xyron-logo.png" alt="Xyron Core ERP" className="w-6 h-6 rounded-lg" />
            <h1 className="font-heading text-lg font-bold text-slate-900">
              Xyron<span className="text-blue-600">Core ERP</span>
            </h1>
          </div>
          <GlobalSearch />
        </header>

        <div className="flex-1 p-4 lg:p-8 page-enter">
          <Outlet />
        </div>
      </main>

      {/* Help Chatbot */}
      <HelpChatbot />
      {/* Onboarding Tour */}
      <OnboardingTour />
    </div>
  );
};

export default Layout;
